import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ticket } from "lucide-react";
import { SEO_PAGES } from "../data/seoPages";
import SEOHead from "../components/SEOHead";
import useHomeOrder from "../hooks/useHomeOrder";

const TICKETED_SLUGS = new Set([
 "nye-cruises-sydney-harbour",
 "vivid-cruises-sydney",
 "australia-day-cruises-sydney-harbour",
 "boxing-day-cruises-sydney",
]);

/* Map admin Home Order IDs → seoPages slugs so the same drag-to-reorder
   from /admin-magic-portal also drives the order of cards on this page. */
const ORDER_ID_TO_SLUG = {
 nye: "nye-cruises-sydney-harbour",
 vivid: "vivid-cruises-sydney",
 "australia-day": "australia-day-cruises-sydney-harbour",
 "boxing-day": "boxing-day-cruises-sydney",
};

export default function TicketedCruisesIndex() {
 const homeOrder = useHomeOrder();
 const bySlug = Object.fromEntries(SEO_PAGES.filter(p => TICKETED_SLUGS.has(p.slug)).map(p => [p.slug, p]));
 const ordered = (homeOrder || [])
  .map(id => bySlug[ORDER_ID_TO_SLUG[id]])
  .filter(Boolean);
 // Append any ticketed pages not represented in the admin order (safety fallback).
 Object.values(bySlug).forEach(p => { if (!ordered.find(x => x.slug === p.slug)) ordered.push(p); });
 const ticketed = ordered;
 return (
  <main className="pt-32 pb-24 bg-[#06060C] text-white min-h-screen" data-testid="ticketed-cruises-index">
   <SEOHead
    title="Ticketed Sydney Harbour Cruises | NYE, Vivid, Aus Day, Boxing Day Tickets"
    description="Buy individual tickets for Sydney's biggest harbour events aboard MV Sydney Magic , New Year's Eve 2026 fireworks, Vivid Sydney 2027, Australia Day 26 Jan 2027 and Boxing Day Sydney to Hobart. Instant Stripe checkout, boarding pass PDF emailed."
    image="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/srsraacs_sydney%20cruises%20boats.png"
    canonical="/ticketed-cruises"
    keywords="NYE cruise tickets Sydney, Vivid Sydney cruise tickets, Australia Day cruise tickets, Boxing Day Sydney to Hobart cruise tickets"
    includeLocalBusiness
    breadcrumbs={[
     { name: "Home", url: "/" },
     { name: "Ticketed Cruises", url: "/ticketed-cruises" },
    ]}
   />
   <div className="mx-auto max-w-7xl px-5 lg:px-10">
    <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.32em] font-bold mb-3">
     <Ticket className="w-3.5 h-3.5" /> Ticketed cruises Sydney Harbour
    </div>
    <h1 className="font-black tracking-tight text-4xl lg:text-7xl mt-3 mb-4 leading-[1]" style={{ fontFamily: "'Playfair Display', serif" }}>
     Buy individual <em className="italic font-light" style={{ color: "#D4AF37" }}>tickets online.</em>
    </h1>
    <p className="text-white/70 max-w-2xl mb-12 text-lg leading-[1.7]">Sydney's biggest harbour events on Magic, sold by the ticket. NYE 2026, Vivid Sydney, Australia Day, Boxing Day Sydney to Hobart. Pick the event, choose your tickets, pay with Stripe in 60 seconds.</p>
    <div className="grid sm:grid-cols-2 gap-6">
     {ticketed.map((p) => (
      <Link key={p.slug} to={`/cruises/${p.slug}`} className="group rounded-3xl ring-1 ring-amber-300/15 bg-white/[0.03] hover:ring-amber-300/40 hover:bg-white/[0.06] overflow-hidden transition-all" data-testid={`ticketed-card-${p.slug}`}>
       <div className="aspect-[16/10] overflow-hidden relative">
        <img src={p.image} alt={p.h1} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06060C] via-[#06060C]/30 to-transparent" />
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-amber-300 text-[#0A0A0A] text-[10px] uppercase tracking-[0.28em] font-black">{p.tag}</span>
       </div>
       <div className="p-7 lg:p-8">
        <h2 className="font-black text-2xl lg:text-3xl mb-3 group-hover:text-amber-300 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{p.h1}</h2>
        <p className="text-white/70 text-base leading-[1.7] mb-5">{p.headline}</p>
        <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.22em] font-black">
         Buy tickets <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </div>
       </div>
      </Link>
     ))}
    </div>
   </div>
  </main>
 );
}
